import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltyHeroProps {
  layout?: 'card' | 'banner' | 'minimal';
  showAvatar?: boolean;
  showTierBadge?: boolean;
  showPoints?: boolean;
  backgroundUrl?: string;
  styleVariant?: string;
}

function AvatarCircle({ firstName, lastName, avatarUrl, size = 'lg' }: {
  firstName: string; lastName: string; avatarUrl: string | null; size?: 'sm' | 'lg';
}) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const sizeClasses = size === 'lg'
    ? 'w-20 h-20 text-2xl'
    : 'w-10 h-10 text-sm';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${firstName} ${lastName}`}
        className={`${sizeClasses} rounded-full object-cover border-2 border-[var(--color-primary)]`}
      />
    );
  }

  return (
    <div className={`${sizeClasses} rounded-full flex items-center justify-center font-bold bg-[var(--color-primary)] text-white`}>
      {initials}
    </div>
  );
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#128100;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view your dashboard
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to see your loyalty status, points, and rewards.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

export function LoyaltyHeroBlock({ props }: { props: LoyaltyHeroProps }) {
  const { data, loading } = useLoyalty();
  const layout = props.layout ?? 'card';
  const showAvatar = props.showAvatar ?? true;
  const showTierBadge = props.showTierBadge ?? true;
  const showPoints = props.showPoints ?? true;

  if (loading) {
    return (
      <div className="loyalty-card p-8 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-[var(--color-surface-border)]" />
          <div className="flex-1 space-y-3">
            <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded" />
            <div className="h-4 w-32 bg-[var(--color-surface-border)] rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const { profile } = data;

  // ─── Minimal layout ──────────────────────────────────────────────────────
  if (layout === 'minimal') {
    return (
      <div className="loyalty-card p-4 flex items-center gap-3">
        {showAvatar && (
          <AvatarCircle
            firstName={profile.firstName}
            lastName={profile.lastName}
            avatarUrl={profile.avatarUrl}
            size="sm"
          />
        )}
        <div className="flex-1 min-w-0">
          <span className="loyalty-heading text-sm text-[var(--color-text)] truncate block">
            {profile.firstName} {profile.lastName}
          </span>
        </div>
        {showTierBadge && profile.tierName && (
          <span
            className="loyalty-tag-skew text-xs font-bold px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: profile.tierColor ?? 'var(--color-primary)' }}
          >
            {profile.tierName}
          </span>
        )}
        {showPoints && (
          <span className="text-[var(--color-primary)] font-bold text-sm whitespace-nowrap">
            {profile.pointsBalance.toLocaleString()} <span className="text-xs opacity-70">PTS</span>
          </span>
        )}
      </div>
    );
  }

  // ─── Banner layout ────────────────────────────────────────────────────────
  if (layout === 'banner') {
    return (
      <div
        className="relative w-full min-h-[200px] flex items-center overflow-hidden"
        style={{
          backgroundImage: props.backgroundUrl ? `url(${props.backgroundUrl})` : undefined,
          backgroundColor: props.backgroundUrl ? undefined : 'var(--color-surface)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {props.backgroundUrl && (
          <div className="absolute inset-0 bg-black/40" />
        )}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-8 flex items-center gap-6">
          {showAvatar && (
            <AvatarCircle
              firstName={profile.firstName}
              lastName={profile.lastName}
              avatarUrl={profile.avatarUrl}
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className={`loyalty-heading text-2xl sm:text-3xl ${props.backgroundUrl ? 'text-white' : 'text-[var(--color-text)]'}`}>
              {profile.firstName} {profile.lastName}
            </h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {showTierBadge && profile.tierName && (
                <span
                  className="loyalty-tag-skew text-xs font-bold px-3 py-1 rounded-full text-white"
                  style={{ backgroundColor: profile.tierColor ?? 'var(--color-primary)' }}
                >
                  {profile.tierName}
                </span>
              )}
              {profile.enrolledAt && (
                <span className={`text-xs ${props.backgroundUrl ? 'text-white/70' : 'text-[var(--color-text-muted)]'}`}>
                  Member since {formatDate(profile.enrolledAt)}
                </span>
              )}
            </div>
          </div>
          {showPoints && (
            <div className="text-right">
              <div className={`text-3xl sm:text-4xl font-bold ${props.backgroundUrl ? 'text-white' : 'text-[var(--color-primary)]'}`}>
                {profile.pointsBalance.toLocaleString()}
              </div>
              <div className={`text-xs font-semibold tracking-wider uppercase ${props.backgroundUrl ? 'text-white/60' : 'text-[var(--color-text-muted)]'}`}>
                PTS
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Card layout (default) ────────────────────────────────────────────────
  return (
    <div className="loyalty-card p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {showAvatar && (
          <AvatarCircle
            firstName={profile.firstName}
            lastName={profile.lastName}
            avatarUrl={profile.avatarUrl}
          />
        )}
        <div className="flex-1 text-center sm:text-left min-w-0">
          <h1 className="loyalty-heading text-2xl sm:text-3xl text-[var(--color-text)]">
            {profile.firstName} {profile.lastName}
          </h1>
          <div className="flex items-center gap-3 mt-2 justify-center sm:justify-start flex-wrap">
            {showTierBadge && profile.tierName && (
              <span
                className="loyalty-tag-skew text-xs font-bold px-3 py-1 rounded-full text-white"
                style={{ backgroundColor: profile.tierColor ?? 'var(--color-primary)' }}
              >
                {profile.tierName}
              </span>
            )}
            {profile.enrolledAt && (
              <span className="text-xs text-[var(--color-text-muted)]">
                Member since {formatDate(profile.enrolledAt)}
              </span>
            )}
          </div>
        </div>
        {showPoints && (
          <div className="text-center sm:text-right">
            <div className="text-4xl sm:text-5xl font-bold text-[var(--color-primary)]">
              {profile.pointsBalance.toLocaleString()}
            </div>
            <div className="text-xs font-semibold tracking-wider uppercase text-[var(--color-text-muted)]">
              PTS
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
